/**
 * Calendar Fixed schedule-preview clocks for RruleBuilder (REBUILD-592).
 * Builds instants in the tenant zone and mirrors SiteTimeZone#overdue_window_end.
 */

import { RRule } from 'rrule'
import { toWallClockString, wallClockToUtcIso } from '@/shared/lib/tenantDateTime'

const ONE_SECOND_MS = 1000
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * ONE_SECOND_MS
const SHORT_MONTH_SEARCH_YEARS = 5
const SHORTEST_MONTH_DAYS = 28
// Intl renders longer years, but they are meaningless in a schedule preview and the formatted
// strings no longer round-trip, so previews beyond year 9999 are hidden.
const MAX_PREVIEW_YEAR = 9999

/**
 * True when `date` is a valid instant whose year can be rendered by the schedule preview.
 * A huge Overdue/Repeats value yields an Invalid Date, which makes
 * `Intl.DateTimeFormat#formatToParts` throw `RangeError: Invalid time value` (REBUILD-629).
 */
export const isRenderablePreviewDate = (date: Date | null | undefined): date is Date =>
  date instanceof Date &&
  Number.isFinite(date.getTime()) &&
  date.getUTCFullYear() <= MAX_PREVIEW_YEAR

/** Previous calendar day for an ISO `YYYY-MM-DD` (UTC noon arithmetic avoids DST gaps). */
export const previousIsoCalendarDay = (isoDate: string): string => {
  const [year, month, day] = isoDate.split('-').map(Number)
  const utcNoon = new Date(Date.UTC(year, month - 1, day, 12, 0, 0))
  utcNoon.setUTCDate(utcNoon.getUTCDate() - 1)
  const previousYear = utcNoon.getUTCFullYear()
  const previousMonth = String(utcNoon.getUTCMonth() + 1).padStart(2, '0')
  const previousDay = String(utcNoon.getUTCDate()).padStart(2, '0')
  return `${previousYear}-${previousMonth}-${previousDay}`
}

/**
 * "If completed now" preview anchor: today's tenant-local calendar date at Starts At, as a
 * tenant wall-clock `YYYY-MM-DDTHH:mm` (independent of the browser zone).
 * Explicit empty Starts At (`''`) suppresses preview (REBUILD-369).
 */
export const buildTenantPreviewAnchorWallClock = (params: {
  startsAt: string | undefined
  tenantTimeZone: string
  now?: Date
}): string | null => {
  if (params.startsAt !== undefined && params.startsAt.trim() === '') return null

  const todayWallClock = toWallClockString(params.now ?? new Date(), params.tenantTimeZone)
  const datePart = todayWallClock.split('T')[0]

  let hour = 0
  let minute = 0
  if (params.startsAt) {
    const [hourString, minuteString] = params.startsAt.split(':')
    hour = Number(hourString) || 0
    minute = Number(minuteString) || 0
  } else {
    const [, timePart] = todayWallClock.split('T')
    const [hourString, minuteString] = timePart.split(':')
    hour = Number(hourString) || 0
    minute = Number(minuteString) || 0
  }

  return `${datePart}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

// Floating time: tenant wall-clock digits stored as a UTC Date, so rrule (which steps in UTC
// when no tzid is set) keeps 07:00 at 07:00 across DST instead of stepping real instants.
const toFloatingDate = (wallClock: string): Date => new Date(`${wallClock}Z`)

const floatingToWallClock = (floating: Date): string => floating.toISOString().slice(0, 16)

/**
 * Mirrors backend add_by_month_day_fallback_occurrences: BYMONTHDAY above a short month's
 * length clamps to that month's last day instead of skipping the month. Floating-time in/out.
 */
const applyShortMonthFallback = (params: {
  byMonthDay: number
  floatingAnchor: Date
  floatingNow: Date
  ruleOccurrence: Date | null
}): Date | null => {
  const { byMonthDay, floatingAnchor, floatingNow, ruleOccurrence } = params
  const searchUntil =
    ruleOccurrence ?? new Date(floatingNow.getTime() + SHORT_MONTH_SEARCH_YEARS * ONE_YEAR_MS)
  const hour = floatingAnchor.getUTCHours()
  const minute = floatingAnchor.getUTCMinutes()
  let cursorYear = floatingAnchor.getUTCFullYear()
  let cursorMonthIndex = floatingAnchor.getUTCMonth()

  while (new Date(Date.UTC(cursorYear, cursorMonthIndex, 1, hour, minute)) < searchUntil) {
    const daysInThisMonth = new Date(Date.UTC(cursorYear, cursorMonthIndex + 1, 0)).getUTCDate()
    if (byMonthDay > daysInThisMonth) {
      const candidate = new Date(
        Date.UTC(cursorYear, cursorMonthIndex, daysInThisMonth, hour, minute),
      )
      if (candidate > floatingNow) {
        return ruleOccurrence && ruleOccurrence < candidate ? ruleOccurrence : candidate
      }
    }
    cursorMonthIndex += 1
    if (cursorMonthIndex > 11) {
      cursorMonthIndex = 0
      cursorYear += 1
    }
  }
  return ruleOccurrence
}

/**
 * Calendar Fixed "Next trigger" preview (REBUILD-263): first RRULE occurrence, seeded at
 * today's tenant-local date at Starts At, strictly after the current tenant wall clock
 * (today's Starts At counts while still in the future). Occurrences are generated in tenant
 * wall-clock time so DST does not shift Starts At. Returns a real UTC instant.
 */
export const computeNextTriggerPreview = (params: {
  structuralRrule: string
  startsAt: string | undefined
  tenantTimeZone: string
  now?: Date
}): Date | null => {
  const zone = params.tenantTimeZone
  const now = params.now ?? new Date()
  const anchorWallClock = buildTenantPreviewAnchorWallClock({
    startsAt: params.startsAt,
    tenantTimeZone: zone,
    now,
  })
  if (!anchorWallClock) return null

  const floatingAnchor = toFloatingDate(anchorWallClock)
  const floatingNow = toFloatingDate(toWallClockString(now, zone))
  let ruleOccurrence: Date | null
  try {
    // dtstart carries the Starts At time-of-day into the generated occurrences (the
    // structural RRULE itself has no BYHOUR/BYMINUTE), matching legacy's dtstart-on-preview.
    const parsedRrule = RRule.fromString(`RRULE:${params.structuralRrule}`)
    ruleOccurrence = new RRule({ ...parsedRrule.origOptions, dtstart: floatingAnchor }).after(
      floatingNow,
    )
  } catch {
    return null
  }

  const byMonthDayMatch = params.structuralRrule.match(/BYMONTHDAY=(\d+)/i)
  const byMonthDay = byMonthDayMatch ? parseInt(byMonthDayMatch[1], 10) : null
  const floatingTrigger =
    byMonthDay && byMonthDay > SHORTEST_MONTH_DAYS
      ? applyShortMonthFallback({ byMonthDay, floatingAnchor, floatingNow, ruleOccurrence })
      : ruleOccurrence
  if (!floatingTrigger) return null

  return new Date(wallClockToUtcIso(floatingToWallClock(floatingTrigger), zone))
}

/**
 * Overdue-after preview: raw end = next trigger + overdue − 1s (Frequency Test Matrix).
 * When endsAtMidnight and the raw end falls on a later tenant-local day than the trigger,
 * clamp to 23:59:59 on the day before the raw end (SiteTimeZone#overdue_window_end).
 * Returns null when the result cannot be rendered (e.g. an absurdly large Overdue).
 */
export const computeOverduePreviewDate = (params: {
  nextExecution: Date
  lateTimeMinutes: number
  endsAtMidnight?: boolean
  tenantTimeZone: string
}): Date | null => {
  const zone = params.tenantTimeZone
  const rawWindowEnd = new Date(
    params.nextExecution.getTime() + params.lateTimeMinutes * 60 * 1000 - ONE_SECOND_MS,
  )
  if (!isRenderablePreviewDate(rawWindowEnd)) return null

  if (!params.endsAtMidnight) return rawWindowEnd

  const triggerDay = toWallClockString(params.nextExecution, zone).slice(0, 10)
  const rawEndDay = toWallClockString(rawWindowEnd, zone).slice(0, 10)
  if (rawEndDay <= triggerDay) return rawWindowEnd

  const clampDay = previousIsoCalendarDay(rawEndDay)
  const clampAtTwentyThreeFiftyNine = new Date(wallClockToUtcIso(`${clampDay}T23:59`, zone))
  return new Date(clampAtTwentyThreeFiftyNine.getTime() + 59 * ONE_SECOND_MS)
}
